'use client'

import { cn } from '@heroui/react'
import React from 'react'

/**
 * Navbar compatibility shim for HeroUI v3.
 * HeroUI v3 removed the Navbar component family.
 * These are thin wrappers using semantic HTML + Tailwind that preserve
 * the same API surface used across the codebase.
 */

// ---- Navbar ----

interface NavbarProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode
  height?: string
  classNames?: {
    base?: string
    wrapper?: string
    item?: string
  }
}

export function Navbar({
  children,
  className,
  classNames,
  height,
  ...props
}: NavbarProps) {
  return (
    <nav
      className={cn(
        'relative flex w-full items-center justify-between',
        classNames?.base,
        className
      )}
      style={height ? { height } : undefined}
      {...props}
    >
      <div className={cn('flex w-full items-center justify-between', classNames?.wrapper)}>
        {children}
      </div>
    </nav>
  )
}

// ---- NavbarBrand ----

interface NavbarBrandProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

export function NavbarBrand({ children, className, ...props }: NavbarBrandProps) {
  return (
    <div className={cn('flex items-center', className)} {...props}>
      {children}
    </div>
  )
}

// ---- NavbarContent ----

interface NavbarContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  justify?: 'start' | 'center' | 'end'
}

export function NavbarContent({ children, className, justify, ...props }: NavbarContentProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2',
        justify === 'start' && 'justify-start',
        justify === 'center' && 'justify-center',
        justify === 'end' && 'ml-auto justify-end',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

// ---- NavbarItem ----

interface NavbarItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

export function NavbarItem({ children, className, ...props }: NavbarItemProps) {
  return (
    <div className={cn('flex items-center', className)} {...props}>
      {children}
    </div>
  )
}

// ---- NavbarMenuItem ----

interface NavbarMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

export function NavbarMenuItem({ children, className, ...props }: NavbarMenuItemProps) {
  return (
    <div className={cn('w-full', className)} {...props}>
      {children}
    </div>
  )
}
